/**
 * Canonical PAI color palette — the single source of truth for the brand
 * colors used across slides, equations, and demos.
 *
 * Keys are semantic color names; values are their hex codes. Everything that
 * needs one of these colors should derive from this object rather than
 * re-listing hex codes, so the palette can be retuned in exactly one place.
 * Current consumers:
 * - the slide editor's color-swatch grid (`SLIDE_COLOR_SWATCHES` in
 *   `slides/shared/slideTheme.ts`)
 * - the generated KaTeX color macros (`katexMacros.ts`), e.g. `\blue{E}`
 *
 * Insertion order is meaningful: it defines the order colors appear in the
 * slides editor's swatch grid (neutrals first, then accents). Add a color by
 * adding one entry here; every consumer picks it up automatically.
 */
export const PAI_PALETTE = {
  white: '#ffffff',
  lightGrey: '#d1d5db',
  grey: '#9ca3af',
  midGrey: '#6b7280',
  darkGrey: '#4b5563',
  black: '#000000',
  blue: '#75cefa', // 3Blue1Brown bright blue
  indigo: '#6366f1',
  pink: '#ec4899',
  brightPink: '#ff7ffc',
  lightGreen: '#6ee787', // bright mint green, tuned to read on black alongside blue/brightPink
  lightRed: '#ff6b6b', // bright coral red, tuned to read on black alongside blue/brightPink
  red: '#dc2626',
  amber: '#f59e0b',
  green: '#16a34a',
  purple: '#9333ea',
} as const;

/** A semantic color name — one of the keys of {@link PAI_PALETTE}. */
export type PaiColorName = keyof typeof PAI_PALETTE;
